"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { ADMIN_DASHBOARD_ITEM, ADMIN_NAV_GROUPS, ADMIN_ALL_ITEMS } from "@/lib/admin/sidebar-config";
interface AdminDropdownProps {
  className?: string;
  onItemClick?: () => void;
}
export function AdminDropdown(t0) {
  const $ = _c(30);
  const {
    className,
    onItemClick
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleClickOutside = function handleClickOutside(event) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleEscape = function handleEscape(event_0) {
        if (event_0.key === "Escape") {
          setIsOpen(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== onItemClick) {
    t5 = () => {
      setIsOpen(false);
      onItemClick?.();
    };
    $[4] = onItemClick;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleItemClick = t5;
  let t6;
  if ($[6] !== className) {
    t6 = cn("relative", className);
    $[6] = className;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isOpen) {
    t7 = () => setIsOpen(!isOpen);
    $[8] = isOpen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = isOpen ? "text-secondary border-secondary" : "border-transparent text-primary hover:text-secondary";
  let t9;
  if ($[10] !== t8) {
    t9 = cn("flex items-center gap-2", "text-sm font-semibold tracking-wide", "border-b-2 pb-0.5 transition-colors duration-200", t8);
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Shield className="w-4 h-4" />;
    t11 = <span>Admin</span>;
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  const t12 = isOpen && "rotate-180";
  let t13;
  if ($[14] !== t12) {
    t13 = cn("w-3.5 h-3.5 transition-transform duration-200", t12);
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13) {
    t14 = <ChevronDown className={t13} />;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== isOpen || $[19] !== t14 || $[20] !== t7 || $[21] !== t9) {
    t15 = <button onClick={t7} className={t9} aria-expanded={isOpen} aria-haspopup="menu" aria-label={"Admin-Men\xFC"} data-testid="admin-dropdown-trigger">{t10}{t11}{t14}</button>;
    $[18] = isOpen;
    $[19] = t14;
    $[20] = t7;
    $[21] = t9;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== handleItemClick || $[24] !== isOpen) {
    t16 = isOpen && <div className="absolute top-full right-0 mt-2 w-56 rounded-2xl eco-glow backdrop-blur-xl bg-surface/95 py-2 z-[100] animate-in fade-in slide-in-from-top-2 duration-150" role="menu" aria-orientation="vertical" data-testid="admin-dropdown-menu"><div className="py-1 px-2">{ADMIN_ALL_ITEMS.map(item => {
          const Icon = item.icon;
          return <Link key={item.id} href={item.href} onClick={handleItemClick} className="group flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors" role="menuitem"><Icon className="w-4 h-4 text-secondary group-hover:text-accent transition-colors" /><span>{item.label}</span></Link>;
        })}</div></div>;
    $[23] = handleItemClick;
    $[24] = isOpen;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15 || $[27] !== t16 || $[28] !== t6) {
    t17 = <div ref={dropdownRef} className={t6}>{t15}{t16}</div>;
    $[26] = t15;
    $[27] = t16;
    $[28] = t6;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  return t17;
}

/**
 * Mobile version of admin menu - renders grouped nav with active state highlighting
 */
export function AdminMobileMenu(t0) {
  const $ = _c(22);
  const {
    onItemClick
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname) {
    t1 = href => pathname.startsWith(href);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isActive = t1;
  const DashboardIcon = ADMIN_DASHBOARD_ITEM.icon;
  let t2;
  if ($[2] !== isActive) {
    t2 = isActive(ADMIN_DASHBOARD_ITEM.href) ? "bg-primary/10 text-primary" : "text-primary hover:bg-primary/5";
    $[2] = isActive;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = cn("flex items-center gap-3 w-full px-3 py-2.5 text-sm font-bold rounded-xl transition-colors", t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isActive) {
    t4 = isActive(ADMIN_DASHBOARD_ITEM.href) ? "text-primary" : "text-on-surface-variant";
    $[6] = isActive;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = cn("w-4 h-4", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <DashboardIcon className={t5} />;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onItemClick || $[13] !== t3 || $[14] !== t6) {
    t7 = <Link href={ADMIN_DASHBOARD_ITEM.href} onClick={onItemClick} className={t3}>{t6}{ADMIN_DASHBOARD_ITEM.label}</Link>;
    $[12] = onItemClick;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isActive || $[17] !== onItemClick) {
    t8 = ADMIN_NAV_GROUPS.map(group => <div key={group.id} className="pt-3"><div className="px-3 pb-1 font-label text-xs uppercase tracking-[0.2em] text-secondary font-bold">{group.label}</div><div className="space-y-1">{group.items.map(item => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return <Link key={item.id} href={item.href} onClick={onItemClick} className={cn("flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium rounded-xl transition-colors", active ? "bg-primary/10 text-primary" : "text-primary hover:bg-primary/5")}><Icon className={cn("w-4 h-4", active ? "text-primary" : "text-on-surface-variant")} />{item.label}</Link>;
        })}</div></div>);
    $[16] = isActive;
    $[17] = onItemClick;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <div className="space-y-1" data-testid="admin-mobile-menu">{t7}{t8}</div>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}